import { useCallback, useEffect, useMemo, useState } from "react";
import type { AiNoticeSettings } from "../services/studio/types";
import type { Channel } from "../shared/host";
import { resolveAiNoticePolicy, type AiNoticePolicy } from "./policy";
import {
  readAcknowledgement,
  removeSupersededAcknowledgements,
  writeAcknowledgement,
  type AiNoticeAcknowledgement,
} from "./storage";
import { reportAiNoticeAcknowledged } from "./telemetry";

export interface UseAiNoticeReturn {
  /** True while the first-run gate must block the pane. */
  readonly gateOpen: boolean;
  /** The current acknowledgement, or null when there is none. */
  readonly acknowledgement: AiNoticeAcknowledgement | null;
  /** Records the acknowledgement. Returns false (and leaves the gate up) if it could not be saved. */
  readonly acknowledge: () => boolean;
  /** True while the notice is open for re-reading from the footer or More tab. */
  readonly reviewOpen: boolean;
  readonly openReview: () => void;
  readonly closeReview: () => void;
  /** AIVA-8663: the notice in force — Studio's text, validity and on/off switch, or the built-in defaults. */
  readonly policy: AiNoticePolicy;
}

interface AckState {
  readonly identity: string | null;
  /** The policy's version + validity the acknowledgement was read under. */
  readonly policyKey: string;
  readonly acknowledgement: AiNoticeAcknowledgement | null;
}

const policyKeyOf = (policy: AiNoticePolicy): string => `${policy.version}|${policy.ttlMs}`;

/**
 * AIVA-6639 — state for the AI disclaimer gate.
 *
 * AIVA-8663: `settings` is the Studio-configured notice from the add-in settings
 * payload. When it is switched off the gate never opens; when its text changes, or
 * it is switched off and back on, the derived version changes and every user is
 * asked again. Absent settings
 * fall back to the built-in notice (see {@link resolveAiNoticePolicy}).
 *
 * The acknowledgement is read synchronously on first render (not in an effect)
 * so the chat surface never paints for a frame before the gate appears.
 */
export function useAiNotice(
  identity: string | null,
  host: Channel | null,
  settings?: AiNoticeSettings | null,
): UseAiNoticeReturn {
  const isEnabled = settings?.isEnabled;
  const text = settings?.text;
  const validityDays = settings?.validityDays;
  const enabledAtUtc = settings?.enabledAtUtc ?? null;
  const policy = useMemo(
    () =>
      resolveAiNoticePolicy(
        isEnabled === undefined && text === undefined && validityDays === undefined
          ? null
          : {
              isEnabled: isEnabled ?? true,
              text: text ?? "",
              validityDays: validityDays ?? Number.NaN,
              enabledAtUtc,
            },
      ),
    [isEnabled, text, validityDays, enabledAtUtc],
  );
  const policyKey = policyKeyOf(policy);

  const [state, setState] = useState<AckState>(() => ({
    identity,
    policyKey,
    acknowledgement: readAcknowledgement(identity, Date.now(), policy),
  }));
  const [reviewOpen, setReviewOpen] = useState(false);

  // Identity can resolve after first render (Studio config arrives later than
  // the token), and so can the Studio notice. Re-read during render — React's
  // "adjust state on prop change" pattern — so a different user never inherits
  // the previous gate state, and a changed notice is checked against its own key.
  let current = state;
  if (state.identity !== identity || state.policyKey !== policyKey) {
    current = { identity, policyKey, acknowledgement: readAcknowledgement(identity, Date.now(), policy) };
    setState(current);
  }

  useEffect(() => {
    removeSupersededAcknowledgements(policy);
  }, [policy]);

  // A pane can stay open for days, and the same user may have AiVA open in
  // another window. Re-read when the pane becomes visible again (catches the
  // validity expiry) and on cross-window `storage` events (an acknowledgement or
  // reset in one window applies to the others without a reload).
  useEffect(() => {
    const resync = () => {
      const acknowledgement = readAcknowledgement(identity, Date.now(), policy);
      setState((prev) =>
        prev.identity === identity &&
        prev.policyKey === policyKey &&
        prev.acknowledgement?.acknowledgedAt === acknowledgement?.acknowledgedAt
          ? prev
          : { identity, policyKey, acknowledgement },
      );
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") resync();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    globalThis.addEventListener("storage", resync);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      globalThis.removeEventListener("storage", resync);
    };
  }, [identity, policy, policyKey]);

  const acknowledge = useCallback((): boolean => {
    const acknowledgement = writeAcknowledgement(identity, host, Date.now(), policy);
    if (!acknowledgement) return false;
    reportAiNoticeAcknowledged();
    setState({ identity, policyKey, acknowledgement });
    return true;
  }, [identity, host, policy, policyKey]);

  const openReview = useCallback(() => setReviewOpen(true), []);
  const closeReview = useCallback(() => setReviewOpen(false), []);

  const gateOpen = policy.isEnabled && current.acknowledgement === null;

  return {
    gateOpen,
    acknowledgement: current.acknowledgement,
    acknowledge,
    // The gate already shows the full notice; never stack the review sheet on it.
    // A switched-off notice has no review sheet either.
    reviewOpen: reviewOpen && !gateOpen && policy.isEnabled,
    openReview,
    closeReview,
    policy,
  };
}
